/**
 * Funnel Chart Component
 *
 * Horizontal bar chart for funnel visualization.
 * Supports clickable stages, conversion rate annotations,
 * and info tooltips on stage labels.
 * Uses static Tailwind class maps to avoid purge issues.
 */

import { InformationCircleIcon } from '@heroicons/react/24/outline';

// Static color maps — Tailwind purges dynamic class names
const BAR_COLORS = {
  blue: [
    'bg-blue-700',
    'bg-blue-600',
    'bg-blue-500',
    'bg-blue-400',
    'bg-blue-300',
    'bg-blue-600',
    'bg-blue-500',
    'bg-blue-400',
    'bg-blue-300',
    'bg-blue-200',
    'bg-blue-600',
    'bg-blue-500',
  ],
  emerald: [
    'bg-emerald-700',
    'bg-emerald-600',
    'bg-emerald-500',
    'bg-emerald-400',
    'bg-emerald-300',
    'bg-emerald-600',
    'bg-emerald-500',
    'bg-emerald-400',
    'bg-emerald-300',
    'bg-emerald-200',
    'bg-emerald-600',
    'bg-emerald-500',
  ],
  violet: [
    'bg-violet-700',
    'bg-violet-600',
    'bg-violet-500',
    'bg-violet-400',
  ],
};

function FunnelSkeleton() {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 animate-pulse">
      <div className="h-5 bg-gray-200 rounded w-48 mb-6" />
      <div className="space-y-4">
        {[100, 80, 60, 40, 25].map((w, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="w-40 h-4 bg-gray-200 rounded" />
            <div className="flex-1 bg-gray-100 rounded-full h-8">
              <div
                className="h-8 bg-gray-200 rounded-full"
                style={{ width: `${w}%` }}
              />
            </div>
            <div className="w-20 h-4 bg-gray-200 rounded" />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function FunnelChart({
  stages = [],
  title,
  onStageClick,
  loading = false,
  colorScheme = 'blue',
}) {
  if (loading) return <FunnelSkeleton />;

  if (!stages || stages.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        {title && (
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
        )}
        <div className="text-center py-8 text-gray-400">
          No funnel data available yet.
        </div>
      </div>
    );
  }

  const maxCount = Math.max(...stages.map((s) => s.count), 1);
  const colors = BAR_COLORS[colorScheme] || BAR_COLORS.blue;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      {title && (
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      )}
      <div className="space-y-1">
        {stages.map((stage, i) => {
          const barWidth = Math.max((stage.count / maxCount) * 100, 2);
          const barColor = colors[i % colors.length];

          return (
            <div key={stage.name}>
              {/* Stage row */}
              <div
                className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${
                  onStageClick
                    ? 'cursor-pointer hover:bg-gray-50 active:bg-gray-100'
                    : ''
                }`}
                onClick={() => onStageClick?.(stage.name)}
                role={onStageClick ? 'button' : undefined}
                tabIndex={onStageClick ? 0 : undefined}
                onKeyDown={(e) => {
                  if (onStageClick && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    onStageClick(stage.name);
                  }
                }}
              >
                {/* Label + info tooltip */}
                <div className="w-44 min-w-[11rem] text-sm text-gray-600 flex items-center gap-1">
                  <span className="truncate">{stage.label}</span>
                  {stage.tooltip && (
                    <span className="relative group flex-shrink-0">
                      <InformationCircleIcon className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
                      <span className="absolute left-6 top-1/2 -translate-y-1/2 z-50 hidden group-hover:block w-64 px-3 py-2 text-xs text-white bg-gray-900 rounded-lg shadow-lg leading-relaxed">
                        {stage.tooltip}
                      </span>
                    </span>
                  )}
                </div>

                {/* Bar */}
                <div className="flex-1 bg-gray-100 rounded-full h-8 relative overflow-hidden">
                  <div
                    className={`h-8 rounded-full ${barColor} transition-all duration-500`}
                    style={{ width: `${barWidth}%` }}
                  />
                </div>

                {/* Count + percentage */}
                <div className="w-24 text-right flex-shrink-0">
                  <span className="text-sm font-semibold text-gray-900">
                    {stage.count.toLocaleString()}
                  </span>
                  <span className="text-xs text-gray-500 ml-1">
                    ({stage.percentage}%)
                  </span>
                </div>
              </div>

              {/* Drop-off annotation between stages */}
              {i < stages.length - 1 && stage.count > 0 && stages[i + 1]?.count < stage.count && (
                <div className="ml-48 text-xs text-gray-400 py-0.5 select-none">
                  &#8595; {stage.count - (stages[i + 1]?.count || 0)} drop-off
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
